import _memoize from 'lodash/memoize';
import { getFont, FONT } from '@tornado-rdk/typography';
import { PRIMARY, makeThemeColor, DEFAULT_MODE, TERTIARY } from '@tornado-rdk/theme';
import { FULL_HD, HD } from '@bedrock-rdk/constants';
import { colors as progressColors, HEIGHT as PROGRESS_HEIGHT } from '../progress/progress.style';
import { identity } from '../utils/sizes';
import { cacheResolver, FOCUSED_Z_INDEX, makeDefaultStylingContext, UNFOCUSED_Z_INDEX } from '../utils/style';
import {
  BORDER_RADIUS,
  BORDER_WIDTH,
  SCALE_FOCUSED,
  FOCUSED_BORDER_ALPHA,
  UNFOCUSED_BORDER_ALPHA,
} from '../utils/focus';
import {
  EXTRA_TITLE_FONT_NAME,
  EXTRA_TITLE_FONT_TYPE,
  TITLE_FONT_NAME,
  TITLE_FONT_TYPE,
  DISPLAY_ICONS,
  BORDER_COLOR,
  ICON_HEIGHT,
  ICON_PADDING_HORIZONTAL,
  ICON_PADDING_VERTICAL,
  ICONS,
  HIGHLIGHT_COLOR,
  INNER_BORDER_COLOR,
  EXTRA_TITLE_LINES,
} from './card.constants';
import { TRANSPARENT } from '../utils/colors';

export { PROGRESS_HEIGHT };

export { TITLES_ORDER, IMAGE_MARGIN_BOTTOM, ICON_HEIGHT, ICON_PADDING_VERTICAL } from './card.constants';

export const defaultContext = makeDefaultStylingContext();

export const getIconViewBox = _memoize(screenSize => ({
  h: ICON_HEIGHT[screenSize],
}));

export const colors = {
  text: makeThemeColor(PRIMARY),
  border: BORDER_COLOR,
  innerBorder: INNER_BORDER_COLOR,
  overlayTop: makeThemeColor(TERTIARY, 0),
  overlayBottom: makeThemeColor(TERTIARY),
  highlight: HIGHLIGHT_COLOR,
};

export const IMAGE_OFFSET = {
  [HD]: BORDER_WIDTH[HD] * 2,
  [FULL_HD]: BORDER_WIDTH[FULL_HD] * 2,
};

export const getIcon = (icons, position) => (icons ? icons[ICONS[position]] : null);

export const makeBasicTextStyle = ({ font, colorMode = DEFAULT_MODE }) => ({
  color: colors.text[colorMode],
  text: {
    ...font,
    verticalAlign: 'middle',
    wordWrap: false,
    textOverflow: '...',
  },
});

export const makeBasicIconStyle = () => ({
  alpha: 0.8,
  y: ICON_PADDING_VERTICAL,
});

export const getDetailsPosition = _memoize((hasHighlightText = true) =>
  hasHighlightText
    ? {
        mountX: 1,
        x: identity,
      }
    : {
        mountX: 0,
        x: 0,
      },
);

export const getInitialStyle = _memoize(
  ({ screenSize = FULL_HD, colorMode = DEFAULT_MODE } = defaultContext) => ({
    zIndex: UNFOCUSED_Z_INDEX,
    Border: {
      alpha: 0,
      zIndex: 1,
      x: -BORDER_WIDTH[screenSize],
      y: -BORDER_WIDTH[screenSize],
      shader: {
        outline: BORDER_WIDTH[screenSize],
        radius: BORDER_RADIUS[screenSize],
        strokeColor: TRANSPARENT,
      },
    },
    Image: {
      zIndex: 2,
      shader: {
        radius: BORDER_RADIUS[screenSize],
        progressHeight: PROGRESS_HEIGHT,
        progressBackgroundColor: progressColors.background[colorMode],
        progressBarColor: progressColors.bar[colorMode],
      },
    },
    TopLeftIcon: {
      zIndex: 3,
      x: ICON_PADDING_HORIZONTAL,
      y: ICON_PADDING_VERTICAL,
    },
    TopRightIcon: {
      zIndex: 3,
      ...makeBasicIconStyle(),
      visible: DISPLAY_ICONS,
      mountX: 1,
      x: width => width - ICON_PADDING_HORIZONTAL,
      y: ICON_PADDING_VERTICAL,
    },
    BottomRightIcon: {
      zIndex: 3,
      mount: 1,
      x: w => w - ICON_PADDING_HORIZONTAL,
      visible: DISPLAY_ICONS,
      alpha: 0.8,
    },
    Incentive: {
      zIndex: 0,
    },
    ExtraTitle: {
      color: colors.text[colorMode],
      text: {
        ...getFont(EXTRA_TITLE_FONT_NAME, screenSize, EXTRA_TITLE_FONT_TYPE),
        verticalAlign: 'middle',
        wordWrap: true,
        maxLines: EXTRA_TITLE_LINES,
        maxLinesSuffix: '...',
      },
      colorMode,
    },
    Title: makeBasicTextStyle({
      font: getFont(TITLE_FONT_NAME, screenSize, TITLE_FONT_TYPE),
      colorMode,
    }),
    Highlight: {
      text: {
        ...getFont(FONT.BERLIN_1, screenSize),
        verticalAlign: 'middle',
        wordWrap: false,
        textOverflow: '...',
        textColor: colors.highlight[colorMode],
      },
    },
    Details: {
      ...makeBasicTextStyle({ font: getFont(FONT.BERLIN_1, screenSize), colorMode }),
      ...getDetailsPosition(),
      alpha: 0.6,
    },
    ProgressBar: {
      mountY: 1,
    },
  }),
  cacheResolver,
);

export const getFocusedStyle = _memoize(
  ({ colorMode }) => ({
    zIndex: FOCUSED_Z_INDEX,
    Border: {
      smooth: {
        alpha: FOCUSED_BORDER_ALPHA,
        scale: SCALE_FOCUSED,
        'shader.strokeColor': colors.border[colorMode],
        'shader.innerColor': colors.innerBorder[colorMode],
        'shader.scale': SCALE_FOCUSED,
      },
    },
    Image: {
      smooth: {
        scale: SCALE_FOCUSED,
        'shader.scale': SCALE_FOCUSED,
      },
    },
  }),
  cacheResolver,
);

export const unFocusedStyle = {
  zIndex: UNFOCUSED_Z_INDEX,
  Border: {
    smooth: {
      alpha: UNFOCUSED_BORDER_ALPHA,
      scale: 1,
      'shader.strokeColor': TRANSPARENT,
      'shader.innerColor': TRANSPARENT,
      'shader.scale': 1.0,
    },
  },
  Image: {
    smooth: {
      scale: 1,
      'shader.scale': 1.0,
    },
  },
};
